import { republishBrandSearchIndexSoon } from '@/app/brands/republishBrandSearchIndex';
import logger from '@/util/logger';
import {
    planBrandAliasRemoval,
    planSharedNameplate,
    planSharedNameplateDenials,
    type SharedNameplateInput,
    type SharedNameplateTable,
} from '@rock/brands';
import { BrandAliasDao, BrandDao, BrandRequestDao, SharedNameplateDao } from '@rock/db-admin';
import type { Brand, BrandAliasKey, BrandRequestId, SharedNameplate, Timestamp, UserId } from '@rock/types';
import { isBrandRequestOpen } from '@rock/types';
import { confusionKey, normalizeBrandName } from '@rock/util';

const log = logger('sharedNameplateActions');
const brandDao = new BrandDao();
const brandAliasDao = new BrandAliasDao();
const brandRequestDao = new BrandRequestDao();
const sharedNameplateDao = new SharedNameplateDao();

/** What the wizard's preview shows before the deny docs land. */
export interface SharedNameplatePreview {
    /** The bare key and every OCR variant the deny docs will occupy. */
    denyKeys: string[];
    /** Keys that map to a brand today and will stop resolving — each with the brand it resolves to. */
    replaces: { key: string; brand: Brand | undefined }[];
    /**
     * A live brand keyed on the bare name (OCR-confusable spellings included): it must be merged into a
     * qualified side first, or its models would be stranded behind a deny doc.
     */
    bareBrand: Brand | undefined;
    /** Sides that still list the bare name as an alias; apply strips it. */
    sidesListingBareName: Brand[];
}

/** Keyed on the bare key and the sides only — the reason and route notes never change what it shows. */
export async function previewSharedNameplate(
    key: string,
    sides: Brand[],
    brands: Brand[],
): Promise<SharedNameplatePreview> {
    const denials = planSharedNameplateDenials({ key, reason: '' }, '' as Timestamp);
    const existing = await Promise.all(denials.map((denial) => brandAliasDao.getBrandAlias(denial.id)));
    const replaced = await Promise.all(
        existing.map(async (alias) =>
            alias?.brandId ? { key: alias.id, brand: await brandDao.getBrand(alias.brandId) } : undefined,
        ),
    );
    const replaces = replaced.filter((entry) => entry !== undefined);
    const bareClass = confusionKey(key);
    return {
        denyKeys: denials.map((denial) => denial.id),
        replaces,
        bareBrand: brands.find(
            (brand) =>
                confusionKey(brand.canonicalKey) === bareClass && !brand.mergedInto && brand.reviewStatus !== 'junk',
        ),
        sidesListingBareName: sides.filter((side) =>
            side.aliases.some((alias) => normalizeBrandName(alias).key === key),
        ),
    };
}

export type ApplySharedNameplateOutcome =
    | { status: 'applied'; nameplate: SharedNameplate }
    | { status: 'refused'; reason: string };

/**
 * Write the nameplate and its deny placeholders, strip the bare name from each side's alias list (its
 * docs are now deny docs, and the display entry would otherwise outrank the qualified names in the
 * typeahead), settle the brand request this came from (and the bare key's own, if pending), and
 * republish the index in the background so every surface routes by the new table within seconds. The
 * caller runs the applies-to guard first; this refuses only what the planner and the preview refuse.
 */
export async function applySharedNameplate(
    input: Omit<SharedNameplateInput, 'now'>,
    table: SharedNameplateTable,
    sides: Brand[],
    brands: Brand[],
    by: UserId,
    fromRequestId?: BrandRequestId,
): Promise<ApplySharedNameplateOutcome> {
    const now = new Date().toISOString() as Timestamp;
    const outcome = planSharedNameplate(
        { ...input, now },
        table.filter((entry) => entry.key !== input.existing?.key),
    );
    if ('refusal' in outcome) return { status: 'refused', reason: outcome.refusal };
    const { nameplate } = outcome.plan;

    const preview = await previewSharedNameplate(nameplate.key, sides, brands);
    if (preview.bareBrand) {
        return {
            status: 'refused',
            reason: `${preview.bareBrand.canonicalName} is keyed on the bare name; merge it into a qualified brand first.`,
        };
    }

    await sharedNameplateDao.applySharedNameplate(outcome.plan);
    log.info('Wrote shared nameplate', { key: nameplate.key, denials: outcome.plan.denials.length });
    try {
        await stripBareNameFromSides(nameplate.key, preview.sidesListingBareName, now);
        await settleRequests([fromRequestId, nameplate.key as BrandRequestId], by, now);
    } finally {
        republishBrandSearchIndexSoon();
    }
    return { status: 'applied', nameplate };
}

/**
 * Un-share a name: the row and its deny placeholders go, so the bare key is free to resolve (or be
 * minted) again. Only deny docs stamped with this nameplate's key are deleted — an OCR variant can be
 * another nameplate's deny doc too — and the keys of the others are returned (and logged). The sides
 * stay as they are. The caller runs the applies-to guard first.
 */
export async function deleteSharedNameplate(nameplate: SharedNameplate): Promise<string[]> {
    const denials = planSharedNameplateDenials(nameplate, nameplate.updatedAt);
    const existing = await Promise.all(denials.map((denial) => brandAliasDao.getBrandAlias(denial.id)));
    const deleteKeys: BrandAliasKey[] = [];
    const keptDenials: string[] = [];
    for (const doc of existing) {
        if (!doc?.denied) continue;
        if (doc.nameplateKey === nameplate.key) deleteKeys.push(doc.id);
        else keptDenials.push(doc.id);
    }
    if (keptDenials.length) {
        log.warn('Left deny docs not stamped with this nameplate', { key: nameplate.key, keys: keptDenials });
    }
    await sharedNameplateDao.applySharedNameplateRemoval(nameplate.key, deleteKeys);
    log.info('Deleted shared nameplate', { key: nameplate.key, denials: deleteKeys.length });
    republishBrandSearchIndexSoon();
    return keptDenials;
}

async function stripBareNameFromSides(key: string, sides: Brand[], now: Timestamp): Promise<void> {
    for (const side of sides) {
        const ownedDocs = await brandAliasDao.findAliasesByBrandId(side.id);
        let current = side;
        for (const alias of side.aliases.filter((name) => normalizeBrandName(name).key === key)) {
            const plan = planBrandAliasRemoval(current, alias, ownedDocs);
            if (!plan) continue;
            await brandDao.applyBrandAliasRemoval(current.id, plan, now);
            current = { ...current, aliases: plan.brandAliases };
        }
    }
}

async function settleRequests(requestIds: (BrandRequestId | undefined)[], by: UserId, now: Timestamp): Promise<void> {
    for (const requestId of new Set(requestIds)) {
        if (!requestId) continue;
        const request = await brandRequestDao.getBrandRequest(requestId);
        if (request && isBrandRequestOpen(request)) {
            await brandRequestDao.resolveBrandRequest(request.id, { kind: 'shared-nameplate', by, at: now }, now);
        }
    }
}
